import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import SearchableSelect from "../ui/SearchableSelect";
import CustomerAutofill from "./form/CustomerAutofill";
import { splitLegacyAddress } from "./form/legacyAddress";
import { Textarea } from "../ui/textarea";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  MapPin, Ticket, User, Phone, Mail, Car, Layers, Tag,
  Clock, Save, Loader2, Check, CalendarIcon, Footprints,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { useTimezone } from "../../contexts/TimezoneContext";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { addHoursToTime } from "../../utils/dateUtils";
import { Calendar } from "../ui/calendar";
import { dialogContentOutsideGuards } from "../../utils/dialogOutsideGuards";
import JobDatesSection from "./JobDatesSection";
import CompletionSection from "./form/CompletionSection";
import JobTaxPanel from "./JobTaxPanel";
import RecurringJobPanel from "./RecurringJobPanel";
import CustomerAddressPicker from "./CustomerAddressPicker";
import AddressSection from "./form/AddressSection";
import { useAddressAutocomplete } from "./form/useAddressAutocomplete";
import { MultiTechSelect } from "./form/MultiTechSelect";
import { TechSplitEditor } from "./form/TechSplitEditor";
import { buildTechSplits, getTechCommission } from "../../utils/job-form-utils";
import JobAttachments, { PendingAttachmentsPicker, uploadJobAttachments } from "./JobAttachments";
import CustomerModal from "../customers/CustomerModal";
import useJobStatuses from "../../hooks/useJobStatuses";

const STORE_FALLBACK_STATUSES = [
  { id: "new-lead", label: "New Lead" }, { id: "estimate", label: "Estimate" },
  { id: "scheduled", label: "Scheduled" }, { id: "in_progress", label: "In Progress" },
  { id: "deposit_collected", label: "Deposit Collected" }, { id: "completed", label: "Completed" },
  { id: "completed_pending_payment", label: "Pending Payment" }, { id: "cancelled", label: "Cancelled" },
];

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const StoreJobFormDialog = ({
  open, onOpenChange, editingJob, customerPrefill, schedulePrefill, ticketPrefill,
  customers, teamMembers, vehicleData,
  onSuccess, variant = "dialog",
}) => {
  const isPage = variant === "page";
  const { getCurrentTime, getToday } = useTimezone();
  const { assignable: statusOptions, getStatus } = useJobStatuses();
  const navigate = useNavigate();

  // Store data
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [jobSources, setJobSources] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [parts, setParts] = useState([]);
  const [payments, setPayments] = useState([]);

  // Form state
  const [form, setForm] = useState({});
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ticketPreview, setTicketPreview] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  // Address picker state — opens when an existing customer is selected.
  // The picker handles its own fetch + auto-skip on single-address.
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [pickerCustomer, setPickerCustomer] = useState(null);
  // Files picked before the job exists — uploaded right after creation.
  const [pendingFiles, setPendingFiles] = useState([]);
  // Fresh tax snapshot from JobTaxPanel override/remove — overlays editingJob
  // so the Financial Summary and tax panel stay in sync without a refetch.
  const [taxSnapshot, setTaxSnapshot] = useState(null);
  // Live tax preview from JobTaxPanel — feeds the Job Completion summary
  // so tax + customer total show before the job is saved.
  const [taxPreview, setTaxPreview] = useState(null);
  // Custom tax override drafted on a NEW job — included in the create payload.
  const [taxOverrideDraft, setTaxOverrideDraft] = useState(null);
  useEffect(() => { setTaxSnapshot(null); setTaxPreview(null); setTaxOverrideDraft(null); }, [open, editingJob]);
  // Linked customer viewer/editor — opens over the job form, closing returns here
  const [customerModal, setCustomerModal] = useState(null);

  const openLinkedCustomer = async () => {
    if (!form.customer_id) return;
    const found = (customers || []).find((c) => c.customer_id === form.customer_id);
    if (found) { setCustomerModal(found); return; }
    try {
      const res = await axios.get(`${API}/customers/${form.customer_id}`, getAuthConfig());
      setCustomerModal(res.data);
    } catch {
      toast.error("Could not load customer");
    }
  };

  const onLinkedCustomerSaved = (updated) => {
    setCustomerModal(updated);
    setForm((prev) => prev.customer_id === updated.customer_id ? {
      ...prev,
      customer_name: updated.name ?? prev.customer_name,
      customer_phone: updated.phone ?? prev.customer_phone,
      customer_phone2: updated.secondary_phone ?? prev.customer_phone2,
      customer_email: updated.email ?? prev.customer_email,
    } : prev);
  };

  // A job clicked inside the customer's Jobs tab — jump to that job's edit page.
  const openCustomerJob = (j) => {
    setCustomerModal(null);
    if (editingJob?.job_id === j.job_id) return;
    if (!isPage) onOpenChange?.(false);
    navigate(`/dashboard/jobs/${j.job_id}/edit`);
  };
  // Google Places autocomplete on the Service Address street input —
  // same hook the single-location form uses.
  const address = useAddressAutocomplete(setForm);

  const isAutomotive = (() => {
    if (!form.store_category_id) return false;
    const cat = categories.find(c => c.category_id === form.store_category_id);
    return cat?.name?.toLowerCase().includes("auto") || cat?.name?.toLowerCase().includes("vehicle");
  })();

  const selectedCategory = categories.find(c => c.category_id === form.store_category_id);
  const subCategories = selectedCategory?.sub_categories || [];
  // Services filtered by selected category — fall back to ALL services if the
  // filter would empty the list (e.g. user's services aren't tagged to a specific
  // category but still need to show up in the dropdown).
  const rawFilteredServices = form.store_category_id && selectedCategory
    ? services.filter(s => (s.category || "").toLowerCase() === (selectedCategory.name || "").toLowerCase())
    : services;
  const filteredServices = rawFilteredServices.length > 0 ? rawFilteredServices : services;
  const showingAllServicesFallback =
    form.store_category_id && rawFilteredServices.length === 0 && services.length > 0;

  const resetForm = useCallback(() => {
    const now = getToday();
    setPendingFiles([]);
    setForm({
      store_location_id: "",
      walk_in: false,  // Default OFF — the location selector only appears when walk-in is toggled on
      customer_name: "", customer_phone: "", customer_phone2: "", customer_email: "",
      customer_phone_ext: "", customer_phone2_ext: "",
      customer_id: "",
      // Customer service address — picked from the address modal on
      // existing-customer select; persisted on the job record so SMS/email
      // placeholders ({{customer_address}} / {{customer_city}} / {{customer_zipcode}})
      // resolve correctly without referencing the customer profile.
      customer_address: "", address_city: "", address_state: "", zip_code: "",
      store_category_id: "", store_sub_category_id: "",
      service_id: "", service_type: "",
      source_id: "",
      vehicle_year: "", vehicle_make: "", vehicle_model: "",
      vehicle_vin: "",
      all_keys_lost: false, vehicle_running: true, has_key: true,
      assigned_to: "",
      additional_technician_ids: [],
      tech_commission_split_mode: "equal",
      tech_commission_split_unit: "percent",
      tech_commission_splits_map: {},
      tech_commission_splits_amount_map: {},
      send_assignment_sms: false,  // default OFF — user opts in per job
      scheduled_date: now,
      scheduled_time: getCurrentTime ? getCurrentTime() : format(new Date(), "h:mm a"),
      schedule_type: "exact",
      scheduled_time_end: "",
      is_recurring: false,
      recurring_interval_value: 1,
      recurring_interval_unit: "months",
      notes: "",
      title: "",
      // Editable date overrides (surfaced in the collapsible Dates section)
      created_at: "",
      completed_at: "",
      status: "new-lead",
      // Completion-section fields (only sent when status = completed*)
      total_charged: "",
      source_commission_type: "percentage",
      source_commission_value: "0",
      technician_commission_type: "percentage",
      technician_commission_value: "",
      additional_technician_id: "",
      additional_tech_commission_type: "percentage",
      additional_tech_commission_value: "50",
      source_extra_fee_enabled: false,
      source_extra_fee_per_job: 0,
      tax_disabled: false,
    });
    setTicketPreview("");
  }, [getToday, getCurrentTime]);

  // When NOT a walk-in, silently auto-assign to the account's default location
  // (or the first active location if no default is set). Also fills a default
  // for legacy/imported jobs being edited without a location (required to save).
  useEffect(() => {
    if (editingJob) {
      if (editingJob.store_location_id) return;
    } else if (form.walk_in) return;
    if (form.store_location_id) return;
    if (!locations || locations.length === 0) return;
    const preferred =
      locations.find((l) => l.is_default && l.active !== false) ||
      locations.find((l) => l.active !== false) ||
      locations[0];
    if (preferred) {
      setForm((f) => ({ ...f, store_location_id: preferred.location_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.walk_in, locations, editingJob]);

  // Initialize form on open
  useEffect(() => {
    if (open) {
      if (editingJob) {
        const linkedCust = editingJob.customer_id
          ? (customers || []).find((c) => c.customer_id === editingJob.customer_id)
          : null;
        setForm({
          store_location_id: editingJob.store_location_id || "",
          walk_in: editingJob.walk_in ?? true,
          customer_name: editingJob.customer_name || linkedCust?.name || "",
          customer_phone: editingJob.customer_phone || linkedCust?.phone || "",
          customer_phone_ext: editingJob.customer_phone_ext || "",
          customer_phone2_ext: editingJob.customer_phone2_ext || "",
          customer_phone2: editingJob.customer_phone2 || linkedCust?.secondary_phone || "",
          customer_email: editingJob.customer_email || linkedCust?.email || "",
          customer_id: editingJob.customer_id || "",
          customer_address: editingJob.customer_address || "",
          address_city: editingJob.address_city || "",
          address_state: editingJob.address_state || "",
          zip_code: editingJob.zip_code || "",
          latitude: editingJob.latitude ?? null,
          longitude: editingJob.longitude ?? null,
          store_category_id: editingJob.store_category_id || "",
          store_sub_category_id: editingJob.store_sub_category_id || "",
          service_id: editingJob.service_id || "",
          duration_hours: editingJob.duration_hours || 1,
          service_type: editingJob.service_type || "",
          source_id: editingJob.source_id || "",
          vehicle_year: editingJob.vehicle_year || "",
          vehicle_make: editingJob.vehicle_make || "",
          vehicle_model: editingJob.vehicle_model || "",
          vehicle_vin: editingJob.vehicle_vin || "",
          all_keys_lost: editingJob.all_keys_lost ?? false,
          vehicle_running: editingJob.vehicle_running ?? true,
          has_key: editingJob.has_key ?? true,
          assigned_to: editingJob.assigned_to || "",
          additional_technician_ids: editingJob.additional_technician_ids
            || (editingJob.additional_technician_id ? [editingJob.additional_technician_id] : []),
          tech_commission_split_mode: editingJob.tech_commission_split_mode || "equal",
          tech_commission_split_unit: editingJob.tech_commission_split_unit || "percent",
          tech_commission_splits_map: Object.fromEntries(
            (editingJob.tech_commission_splits || []).filter(s => s.percent != null).map(s => [s.technician_id, s.percent])
          ),
          tech_commission_splits_amount_map: Object.fromEntries(
            (editingJob.tech_commission_splits || []).filter(s => s.amount != null).map(s => [s.technician_id, s.amount])
          ),
          send_assignment_sms: false,  // default OFF on edit — don't re-text on update
          scheduled_date: editingJob.scheduled_date ? new Date(editingJob.scheduled_date) : getToday(),
          scheduled_time: editingJob.scheduled_time || "",
          schedule_type: editingJob.schedule_type || (editingJob.scheduled_time_end ? "between" : "exact"),
          scheduled_time_end: editingJob.scheduled_time_end || "",
          is_recurring: !!editingJob.is_recurring,
          recurring_interval_value: editingJob.recurring_interval_value || 1,
          recurring_interval_unit: editingJob.recurring_interval_unit || "months",
          notes: editingJob.notes || "",
          title: editingJob.title || "",
          created_at: editingJob.created_at || "",
          completed_at: editingJob.completed_at || "",
          status: editingJob.status || "new-lead",
          // Completion-section
          total_charged: editingJob.total_charged ?? "",
          source_commission_type: editingJob.source_commission_type || "percentage",
          source_commission_value: editingJob.source_commission_value?.toString() || "0",
          technician_commission_type: editingJob.technician_commission_type || "percentage",
          technician_commission_value: editingJob.technician_commission_value != null ? editingJob.technician_commission_value.toString() : "",
          additional_technician_id: editingJob.additional_technician_id || "",
          additional_tech_commission_type: editingJob.additional_tech_commission_type || "percentage",
          additional_tech_commission_value: editingJob.additional_tech_commission_value?.toString() || "50",
          source_extra_fee_enabled: editingJob.source_extra_fee_enabled || false,
          source_extra_fee_per_job: editingJob.source_extra_fee_per_job || 0,
          tax_disabled: editingJob.tax_disabled ?? false,
        });
        setTicketPreview(editingJob.ticket_number || "");
        // Hydrate parts/payments for completion section
        setParts(Array.isArray(editingJob.parts) ? editingJob.parts.map(p => ({
          name: p.name || "",
          cost: p.cost ?? "",
          provider: p.provider || "company",
          item_id: p.item_id,
        })) : []);
        setPayments(Array.isArray(editingJob.payments) ? editingJob.payments.map(p => ({
          payment_type_id: p.payment_type_id || "",
          amount: p.amount ?? "",
          collected_by: p.collected_by || "company",
        })) : []);
      } else {
        resetForm();
        setParts([]);
        setPayments([]);
        if (customerPrefill) {
          // Quick "New Job for Customer" — carry over only the customer fields
          const c = customerPrefill;
          setForm((f) => ({
            ...f,
            customer_id: c.customer_id || "",
            customer_name: c.customer_name || "",
            customer_phone: c.customer_phone || "",
            customer_email: c.customer_email || "",
            customer_address: c.customer_address || "",
            address_city: c.address_city || "",
            address_state: c.address_state || "",
            zip_code: c.zip_code || "",
          }));
        }
        if (schedulePrefill) {
          // Timeline drag-to-create: seed schedule fields
          const s = schedulePrefill;
          setForm((f) => ({
            ...f,
            scheduled_date: s.scheduled_date ? new Date(`${s.scheduled_date}T00:00:00`) : f.scheduled_date,
            scheduled_time: s.scheduled_time || f.scheduled_time,
            schedule_type: "exact",
            duration_hours: s.duration_hours || f.duration_hours,
            assigned_to: s.assigned_to || f.assigned_to,
          }));
        }
        if (ticketPrefill) {
          // Pasted-ticket prefill — mirrors the single-location form mapping
          const d = ticketPrefill;
          const noteLines = [];
          if (d.job_reference) noteLines.push(`Ticket Ref: ${d.job_reference}`);
          if (d.technician_name && !d.matched_technician_id) noteLines.push(`Tech: ${d.technician_name} (not matched)`);
          if (d.parts_cost && d.parts_cost > 0) noteLines.push(`Parts cost: $${d.parts_cost}`);
          setForm((f) => ({
            ...f,
            source_id: d.matched_source_id || "",
            service_id: d.matched_service_id || "",
            assigned_to: d.matched_technician_id || "",
            title: d.job_type || d.job_reference || "Closing Ticket Job",
            customer_id: "",
            customer_name: d.customer_name || "",
            customer_phone: d.customer_phone || "",
            customer_phone2: d.customer_phone2 || "",
            customer_phone_ext: d.customer_phone_ext || "",
            customer_phone2_ext: d.customer_phone2_ext || "",
            customer_address: d.address_street || d.customer_address || "",
            address_city: d.address_city || "",
            address_state: d.address_state || "",
            zip_code: d.zip_code || "",
            scheduled_date: d.scheduled_date ? new Date(`${d.scheduled_date}T12:00:00`) : f.scheduled_date,
            scheduled_time: d.scheduled_time || f.scheduled_time,
            schedule_type: d.scheduled_time_end ? "between" : "exact",
            scheduled_time_end: d.scheduled_time_end || "",
            status: d.total_amount ? "completed" : (d.default_status || "new-lead"),
            total_charged: d.total_amount || "",
            source_commission_type: d.source_commission_type || "percentage",
            source_commission_value: d.source_commission_value ?? "0",
            notes: [d.notes, noteLines.join("\n")].filter(Boolean).join("\n"),
          }));
          if (d.parts_cost && d.parts_cost > 0) {
            setParts([{ name: "Parts", cost: d.parts_cost, provider: "company" }]);
          }
        }
      }
    }
    // NOTE: deliberately excludes resetForm/getToday — their identities can
    // change on unrelated context re-renders, which used to re-run this
    // effect mid-edit and silently reset the user's unsaved changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingJob, customerPrefill, schedulePrefill, ticketPrefill]);

  // Autofill Total Charged (pre-tax, pre-credit-fee) from the job's linked
  // invoice when the job is being marked completed and no total has been
  // entered yet. Also captures the invoice's credit fee for the breakdown.
  const invAutofillRef = React.useRef(null);
  const [linkedInvFee, setLinkedInvFee] = useState(null);
  useEffect(() => { invAutofillRef.current = null; setLinkedInvFee(null); }, [open, editingJob]);
  useEffect(() => {
    const jobId = editingJob?.job_id;
    const isDone = form.status === "completed" || form.status === "completed_pending_payment";
    if (!open || !jobId || !isDone) return;
    if (invAutofillRef.current === jobId) return;
    invAutofillRef.current = jobId;
    const wantAutofill = !(parseFloat(form.total_charged) > 0);
    const token = localStorage.getItem("token");
    axios.get(`${API}/invoices?job_id=${jobId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        const docs = (Array.isArray(res.data) ? res.data : [])
          .filter((d) => d.document_type !== "estimate" && d.status !== "cancelled");
        if (!docs.length) { setLinkedInvFee({ amount: 0 }); return; }
        docs.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        const inv = docs[0];
        const fee = inv.credit_fee_enabled ? (parseFloat(inv.credit_fee_amount) || 0) : 0;
        setLinkedInvFee({ amount: fee, value: inv.credit_fee_value || 0, type: inv.credit_fee_type || "percentage" });
        const preTax = Math.round(((inv.total || 0) - (inv.tax_amount || 0) - fee) * 100) / 100;
        if (wantAutofill && preTax > 0) {
          setForm((prev) => (parseFloat(prev.total_charged) > 0 ? prev : { ...prev, total_charged: String(preTax) }));
          toast.info(`Total charged auto-filled from Invoice #${inv.invoice_number} — $${preTax.toFixed(2)} (pre-tax${fee > 0 ? ", excl. credit fee" : ""})`);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.status, form.total_charged, editingJob?.job_id]);

  // Fetch store locations, categories & services
  // Re-fires when the picked store_location_id changes so the dropdowns
  // (Sources / Job Types / Payment Types) get filtered to that location
  // + globals. Single-location accounts pass nothing → unchanged behaviour.
  useEffect(() => {
    if (!open) return;
    const config = getAuthConfig();
    const locQ = form.store_location_id ? `?location_id=${encodeURIComponent(form.store_location_id)}` : "";
    Promise.all([
      axios.get(`${API}/store/locations`, config).catch(() => ({ data: { locations: [] } })),
      axios.get(`${API}/settings/service-categories`, config).catch(() => ({ data: [] })),
      axios.get(`${API}/settings/job-types${locQ}`, config).catch(() => ({ data: [] })),
      axios.get(`${API}/settings/job-sources${locQ}`, config).catch(() => ({ data: [] })),
      axios.get(`${API}/settings/payment-types${locQ}`, config).catch(() => ({ data: [] })),
    ]).then(([locRes, catRes, svcRes, srcRes, ptRes]) => {
      setLocations((locRes.data.locations || []).filter(l => l.active));
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
      setServices(Array.isArray(svcRes.data) ? svcRes.data : []);
      setJobSources(Array.isArray(srcRes.data) ? srcRes.data : []);
      setPaymentTypes(Array.isArray(ptRes.data) ? ptRes.data : []);
    });
  }, [open, form.store_location_id]);

  // Fetch ticket preview when location changes
  useEffect(() => {
    if (!form.store_location_id || editingJob) return;
    const config = getAuthConfig();
    axios.get(`${API}/store/ticket-preview/${form.store_location_id}`, config)
      .then(res => setTicketPreview(res.data.ticket_number || ""))
      .catch(() => setTicketPreview(""));
  }, [form.store_location_id, editingJob]);

  const selectCustomer = (customer) => {
    // Legacy records may keep the whole address in one string — split it so
    // every address field autofills (street/city/state/zip).
    const legacy = splitLegacyAddress(customer);
    setForm(prev => ({
      ...prev,
      customer_id: customer.customer_id,
      customer_name: customer.name,
      customer_phone: customer.phone || "",
      customer_phone2: customer.secondary_phone || prev.customer_phone2 || "",
      customer_email: customer.email || "",
      // Pre-fill from the customer's legacy fields. The picker overwrites
      // these if the user picks a saved address; if it auto-skips
      // (single-address customer) these stay — so the job still has a
      // populated street/city/state/zip block.
      customer_address: legacy.street || prev.customer_address || "",
      address_city: legacy.city || prev.address_city || "",
      address_state: legacy.state || prev.address_state || "",
      zip_code: legacy.zip || prev.zip_code || "",
    }));
    setCustomerSearchOpen(false);
    // Open the address picker for this customer. The picker auto-skips
    // when there's only 1 saved address (1b) so this is friction-free.
    setPickerCustomer(customer);
    setShowAddressPicker(true);
  };

  const handlePickedAddress = useCallback((addr) => {
    if (!addr) return;
    // Saved address rows can also be legacy one-line strings — split them so
    // city/state/zip always populate.
    const legacy = splitLegacyAddress({
      address: addr.address_line1, city: addr.city, state: addr.state, zip_code: addr.zip_code,
    });
    setForm(prev => ({
      ...prev,
      customer_address: legacy.street || "",
      address_city: legacy.city || "",
      address_state: legacy.state || "",
      zip_code: legacy.zip || "",
    }));
  }, []);

  const closeAddressPicker = useCallback(() => setShowAddressPicker(false), []);

  const buildTitle = () => {
    const titleParts = [];
    if (selectedCategory) titleParts.push(selectedCategory.name);
    const sub = subCategories.find(s => s.sub_id === form.store_sub_category_id);
    if (sub) titleParts.push(sub.name);
    const svc = services.find(s => s.service_id === form.service_id);
    if (svc) titleParts.push(svc.name);
    if (form.vehicle_make) titleParts.push(`${form.vehicle_year || ""} ${form.vehicle_make} ${form.vehicle_model || ""}`.trim());
    return titleParts.join(" - ") || "Store Job";
  };

  const handleSubmit = async () => {
    if (!form.store_location_id) { toast.error("Store location is required"); return; }
    if (!form.customer_name && !form.customer_id) { toast.error("Customer information is required"); return; }
    if (!form.customer_id && !form.customer_phone) { toast.error("Customer phone is required"); return; }

    const extraTechs = (form.additional_technician_ids || []).filter(t => t && t !== form.assigned_to);
    if (form.tech_commission_split_mode === "custom" && form.tech_commission_split_unit !== "amount" && extraTechs.length > 0) {
      const ids = [form.assigned_to, ...extraTechs].filter(Boolean);
      const eq = 100 / ids.length;
      const total = ids.reduce((s, id) => {
        const v = parseFloat((form.tech_commission_splits_map || {})[id]);
        return s + (isNaN(v) ? eq : v);
      }, 0);
      if (Math.abs(total - 100) >= 0.5) {
        toast.error(`Custom commission split must total 100% (currently ${Math.round(total * 100) / 100}%)`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const config = getAuthConfig();
      const title = form.title || buildTitle();
      const payload = {
        title,
        customer_id: form.customer_id || "",
        ...(!editingJob && ticketPrefill ? { force_new_customer: true } : {}),
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_phone_ext: form.customer_phone_ext || "",
        customer_phone2_ext: form.customer_phone2_ext || "",
        customer_phone2: form.customer_phone2?.trim() || null,
        customer_email: form.customer_email,
        // Customer service address — picked from the address modal AND/OR
        // typed directly. Server auto-saves typed-in addresses to the
        // customer's address book via upsert_address_from_job().
        customer_address: form.customer_address || "",
        address_city: form.address_city || "",
        address_state: form.address_state || "",
        zip_code: form.zip_code || "",
        store_location_id: form.store_location_id,
        walk_in: form.walk_in,
        store_category_id: form.store_category_id || null,
        store_sub_category_id: form.store_sub_category_id || null,
        service_id: form.service_id || null,
        service_type: form.service_type || (isAutomotive ? "locksmith" : "other"),
        source_id: form.source_id || null,
        vehicle_year: form.vehicle_year || null,
        vehicle_make: form.vehicle_make || null,
        vehicle_model: form.vehicle_model || null,
        vehicle_vin: form.vehicle_vin || null,
        all_keys_lost: isAutomotive ? form.all_keys_lost : null,
        vehicle_running: isAutomotive ? form.vehicle_running : null,
        has_key: isAutomotive ? form.has_key : null,
        assigned_to: form.assigned_to || null,
        // Multi-technician assignment — backend dedupes, folds legacy field,
        // and computes the commission split (equal or custom percentages).
        additional_technician_ids: (form.additional_technician_ids || []).filter(t => t && t !== form.assigned_to).length > 0
          ? (form.additional_technician_ids || []).filter(t => t && t !== form.assigned_to)
          : null,
        ...(() => {
          const { mode, unit, splits } = buildTechSplits(
            form.assigned_to,
            (form.additional_technician_ids || []).filter(t => t && t !== form.assigned_to),
            form.tech_commission_split_mode,
            form.tech_commission_splits_map,
            form.tech_commission_split_unit || "percent",
            form.tech_commission_splits_amount_map
          );
          return { tech_commission_split_mode: mode, tech_commission_split_unit: unit, tech_commission_splits: splits };
        })(),
        // Local literal date (YYYY-MM-DD) — same as the single-location form.
        // toISOString() shifted evening creates past UTC midnight, landing
        // the job on TOMORROW in the Timeline/Calendar views.
        scheduled_date: (() => {
          const d = form.scheduled_date instanceof Date && !isNaN(form.scheduled_date) ? form.scheduled_date : new Date();
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        })(),
        scheduled_time: form.scheduled_time || null,
        schedule_type: form.schedule_type === "between" && form.scheduled_time_end ? "between" : "exact",
        scheduled_time_end: form.schedule_type === "between" ? (form.scheduled_time_end || null) : null,
        duration_hours: parseFloat(form.duration_hours) || 1,
        latitude: form.latitude ?? null,
        longitude: form.longitude ?? null,
        notes: form.notes || null,
        status: form.status || "scheduled",
        ticket_number: ticketPreview || null,
        send_assignment_sms: !!(form.assigned_to && form.send_assignment_sms),
        tax_disabled: !!form.tax_disabled,
        ...(!editingJob && taxOverrideDraft ? { tax_override: taxOverrideDraft } : {}),
        // Editable dates from the collapsible Dates section
        ...(form.created_at ? { created_at: form.created_at } : {}),
        ...(form.completed_at ? { completed_at: form.completed_at } : {}),
      };

      // Attach completion data when status is completed*
      if (form.status === "completed" || form.status === "completed_pending_payment") {
        payload.total_charged = parseFloat(form.total_charged) || 0;
        payload.parts = parts.map(p => ({
          name: p.name,
          cost: parseFloat(p.cost) || 0,
          provider: p.provider || "company",
          ...(p.item_id ? { item_id: p.item_id } : {}),
        }));
        payload.payments = payments.map(p => ({
          payment_type_id: p.payment_type_id,
          amount: parseFloat(p.amount) || 0,
          collected_by: p.collected_by || "company",
        }));
        payload.source_commission_type = form.source_commission_type;
        payload.source_commission_value = parseFloat(form.source_commission_value) || 0;
        payload.technician_commission_type = form.technician_commission_type;
        payload.technician_commission_value = parseFloat(form.technician_commission_value) || 0;
        payload.source_extra_fee_enabled = !!form.source_extra_fee_enabled;
        payload.source_extra_fee_per_job = parseFloat(form.source_extra_fee_per_job) || 0;
      }

      payload.is_recurring = !!form.is_recurring;
      payload.recurring_interval_value = form.is_recurring ? (parseInt(form.recurring_interval_value, 10) || 1) : null;
      payload.recurring_interval_unit = form.is_recurring ? (form.recurring_interval_unit || "months") : null;

      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.job_id}`, payload, config);
        toast.success("Job updated");
      } else {
        const res = await axios.post(`${API}/jobs`, payload, config);
        toast.success("Job created");
        if (pendingFiles.length > 0 && res.data?.job_id) {
          const { uploaded, failed } = await uploadJobAttachments(res.data.job_id, pendingFiles);
          if (uploaded.length) toast.success(`${uploaded.length} attachment${uploaded.length > 1 ? "s" : ""} uploaded`);
          if (failed.length) toast.error(`Failed to upload: ${failed.map((f) => f.file.name).join(", ")} — you can re-upload from the job details`);
        }
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save job");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // ── Commission auto-fill (multi-location fix) ──
  // Mirrors the single-location form: apply the technician's configured rate
  // (per-service > per-source > base) and the job source's commission config.
  const findTech = (id) => teamMembers.find(t => (t.member_id || t.technician_id) === id);

  const applyTechCommission = (next, { onlyIfPristine = false } = {}) => {
    if (!next.assigned_to) return next;
    if (onlyIfPristine && parseFloat(next.technician_commission_value)) return next;
    const comm = getTechCommission(findTech(next.assigned_to), next.source_id, next.service_id);
    if (comm.value === "" || comm.value == null) return next;
    return { ...next, technician_commission_type: comm.type, technician_commission_value: String(comm.value) };
  };

  const handleTechSelect = (v) => {
    setForm(prev => {
      let next = { ...prev, assigned_to: v };
      if (v) {
        // New tech → always use their configured rate (empty when none set)
        const comm = getTechCommission(findTech(v), prev.source_id, prev.service_id);
        next.technician_commission_type = comm.type;
        next.technician_commission_value = comm.value === "" || comm.value == null ? "" : String(comm.value);
      }
      return next;
    });
  };

  const handleSourceChange = (v) => {
    setForm(prev => {
      let next = { ...prev, source_id: v };
      const source = v ? jobSources.find(s => s.source_id === v) : null;
      if (source) {
        next.source_commission_type = source.commission_type || "percentage";
        next.source_commission_value = source.commission_value != null ? String(source.commission_value) : "0";
        next.source_extra_fee_enabled = !!source.extra_fee_enabled;
        next.source_extra_fee_per_job = source.extra_fee_per_job || 0;
      } else if (!v) {
        next.source_commission_type = "percentage";
        next.source_commission_value = "0";
        next.source_extra_fee_enabled = false;
        next.source_extra_fee_per_job = 0;
      }
      // Tech may have a source-specific rate
      return applyTechCommission(next);
    });
  };

  const handleStatusChange = (v) => {
    setForm(prev => {
      let next = { ...prev, status: v };
      if ((v === "completed" || v === "completed_pending_payment") && prev.assigned_to) {
        next = applyTechCommission(next, { onlyIfPristine: true });
      }
      return next;
    });
  };

  const formBody = (
    <div className={isPage ? "space-y-5" : "px-5 py-4 space-y-5"}>

            {/* ── Job Status (top of body) ── */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40 border border-border" data-testid="store-status-block">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getStatus(form.status)?.color || "#3b82f6" }} />
                <Label className="text-foreground text-sm font-medium">Job Status</Label>
              </div>
              <Select value={form.status || "scheduled"} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-44 bg-background border-border" data-testid="store-job-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(statusOptions.length ? statusOptions : STORE_FALLBACK_STATUSES).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                  {form.status && statusOptions.length > 0 && !statusOptions.some((s) => s.id === form.status) && (
                    <SelectItem value={form.status}>{getStatus(form.status)?.label || form.status} (archived)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* ── Section 1: Location & Ticket ── */}
            <div className="space-y-3" data-testid="store-location-section">
              <div className="flex items-center gap-2 text-blue-500">
                <MapPin className="w-4 h-4" />
                <span className="text-sm font-semibold">Store Location</span>
                <span className="text-red-400 text-xs">*</span>
              </div>

              {/* Walk-In Toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.walk_in}
                  onCheckedChange={(v) => update("walk_in", v)}
                  data-testid="walk-in-toggle"
                />
                <Label className="text-sm text-foreground flex items-center gap-1.5 cursor-pointer">
                  <Footprints className="w-3.5 h-3.5 text-muted-foreground" />
                  Walk-In Customer
                </Label>
              </div>

              {/* Location selector — always visible so multi-location accounts
                  can pick which location the job belongs to. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Location</Label>
                  <Select value={form.store_location_id} onValueChange={(v) => update("store_location_id", v)}>
                    <SelectTrigger className="bg-muted/50 border-border" data-testid="store-location-select">
                      <SelectValue placeholder="Select store location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>
                          <span className="flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-blue-400" />
                            {loc.name} {loc.code && <span className="text-muted-foreground text-xs">({loc.code})</span>}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs flex items-center gap-1.5">
                    <Ticket className="w-3 h-3" /> Ticket Number
                  </Label>
                  <div className="h-9 px-3 flex items-center bg-muted/30 border border-border rounded-md text-sm text-foreground font-mono" data-testid="ticket-number-display">
                    {ticketPreview || <span className="text-muted-foreground italic">Select a location</span>}
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-border" />

            {/* ── Section 2: Customer (unified — suggestions while typing) ── */}
            <div className="space-y-3" data-testid="store-customer-section">
              <div className="flex items-center gap-2 text-blue-500">
                <User className="w-4 h-4" />
                <span className="text-sm font-semibold">Customer</span>
              </div>
              <CustomerAutofill
                values={form}
                onChange={(key, value) => update(key, value)}
                customers={customers}
                onSelectCustomer={selectCustomer}
                onOpenCustomer={openLinkedCustomer}
                onUnlink={() =>
                  // Clear everything the link autofilled so a newly typed
                  // customer can't inherit the previous customer's address.
                  setForm((prev) => ({
                    ...prev,
                    customer_id: "", customer_name: "", customer_phone: "",
                    customer_phone2: "", customer_email: "",
                    customer_phone_ext: "", customer_phone2_ext: "",
                    customer_address: "", address_city: "", address_state: "", zip_code: "",
                  }))
                }
                inputClassName="bg-muted/50 border-border"
                testIds={{
                  name: "store-customer-name", phone: "store-customer-phone",
                  phone2: "store-customer-phone2", email: "store-customer-email",
                  suggestions: "store-customer-suggestions",
                  linked: "store-customer-linked-chip", unlink: "store-customer-unlink-btn",
                  view: "store-customer-view-btn",
                }}
              />
            </div>

            <Separator className="bg-border" />

            {/* ── Service Address (Google Places autocomplete) ── */}
            <div data-testid="store-address-section">
              <AddressSection
                formData={form} setFormData={setForm}
                addressSuggestions={address.addressSuggestions}
                showAddressSuggestions={address.showAddressSuggestions}
                addressLoading={address.addressLoading}
                handleAddressChange={address.handleAddressChange}
                selectAddress={address.selectAddress}
                setShowAddressSuggestions={address.setShowAddressSuggestions}
              />
            </div>

            <Separator className="bg-border" />

            {/* ── Section 3: Category, Sub-Category & Service ── */}
            <div className="space-y-3" data-testid="store-category-section">
              <div className="flex items-center gap-2 text-blue-500">
                <Layers className="w-4 h-4" />
                <span className="text-sm font-semibold">Category & Service</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Category</Label>
                  <Select key={`cat-${categories.length}`} value={form.store_category_id} onValueChange={(v) => { update("store_category_id", v); update("store_sub_category_id", ""); update("service_id", ""); }}>
                    <SelectTrigger className="bg-muted/50 border-border" data-testid="store-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          <span className="flex items-center gap-2">
                            <Layers className="w-3 h-3 text-blue-400" />
                            {cat.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Sub-Category</Label>
                  <Select
                    key={`sub-${subCategories.length}`}
                    value={form.store_sub_category_id}
                    onValueChange={(v) => update("store_sub_category_id", v)}
                    disabled={!form.store_category_id || subCategories.length === 0}
                  >
                    <SelectTrigger className="bg-muted/50 border-border" data-testid="store-subcategory-select">
                      <SelectValue placeholder={subCategories.length === 0 ? "No sub-categories" : "Select sub-category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map(sub => (
                        <SelectItem key={sub.sub_id} value={sub.sub_id}>
                          <span className="flex items-center gap-2">
                            <Tag className="w-3 h-3 text-blue-400" />
                            {sub.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Service + Source selection (2-column) — both use
                  SearchableSelect so Stores with 10+ services / sources get
                  an inline filter without changing the visual hierarchy. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Service</Label>
                  <SearchableSelect
                    value={form.service_id}
                    onChange={(v) => {
                      const svc = services.find(s => s.service_id === v);
                      setForm(prev => {
                        const dur = svc?.default_duration || prev.duration_hours || 1;
                        const next = { ...prev, service_id: v, duration_hours: dur };
                        if (svc) next.service_type = svc.name;
                        // Service duration changed → recompute Between-Hours end time
                        if (next.schedule_type === "between" && next.scheduled_time) {
                          next.scheduled_time_end = addHoursToTime(next.scheduled_time, dur) || next.scheduled_time_end;
                        }
                        // Tech may have a service-specific commission rate
                        return applyTechCommission(next);
                      });
                    }}
                    options={filteredServices.map(svc => ({
                      value: svc.service_id,
                      label: svc.name,
                      hint: svc.default_duration ? `${svc.default_duration}h` : undefined,
                    }))}
                    placeholder={filteredServices.length === 0 ? "No services available" : "Select service"}
                    triggerClassName="bg-muted/50 border-border"
                    data-testid="store-service-select"
                  />
                  {showingAllServicesFallback && (
                    <p className="text-[10px] text-muted-foreground" data-testid="store-services-fallback-hint">
                      Showing all services (none tagged for this category).
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Job Source</Label>
                  <SearchableSelect
                    value={form.source_id}
                    onChange={handleSourceChange}
                    options={jobSources.map(src => ({
                      value: src.source_id,
                      label: src.name,
                    }))}
                    placeholder={jobSources.length === 0 ? "No sources configured" : "Select source"}
                    allowEmpty
                    emptyLabel="-- None --"
                    triggerClassName="bg-muted/50 border-border"
                    data-testid="store-source-select"
                  />
                </div>
              </div>
            </div>

            {/* ── Section 4: Automotive Details (conditional) ── */}
            {isAutomotive && (
              <>
                <Separator className="bg-border" />
                <div className="space-y-3" data-testid="store-automotive-section">
                  <div className="flex items-center gap-2 text-blue-500">
                    <Car className="w-4 h-4" />
                    <span className="text-sm font-semibold">Vehicle Details</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Year</Label>
                      <Select value={form.vehicle_year} onValueChange={(v) => update("vehicle_year", v)}>
                        <SelectTrigger className="bg-muted/50 border-border" data-testid="store-vehicle-year">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleData.years?.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Make</Label>
                      <Select value={form.vehicle_make} onValueChange={(v) => { update("vehicle_make", v); update("vehicle_model", ""); }}>
                        <SelectTrigger className="bg-muted/50 border-border" data-testid="store-vehicle-make">
                          <SelectValue placeholder="Make" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicleData.makes?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-muted-foreground text-xs">Model</Label>
                      <Select value={form.vehicle_model} onValueChange={(v) => update("vehicle_model", v)} disabled={!form.vehicle_make}>
                        <SelectTrigger className="bg-muted/50 border-border" data-testid="store-vehicle-model">
                          <SelectValue placeholder="Model" />
                        </SelectTrigger>
                        <SelectContent>
                          {(vehicleData.models_by_make?.[form.vehicle_make] || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs">VIN (Vehicle Identification Number)</Label>
                    <Input value={form.vehicle_vin} onChange={(e) => update("vehicle_vin", e.target.value.toUpperCase())}
                      className="bg-muted/50 border-border font-mono" placeholder="1HGCM82633A004352" maxLength={17} data-testid="store-vehicle-vin" />
                  </div>

                  {/* Automotive Checkboxes */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                    <label className="flex items-center gap-2.5 cursor-pointer p-2.5 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 transition-colors" data-testid="store-all-keys-lost">
                      <Checkbox checked={form.all_keys_lost} onCheckedChange={(v) => update("all_keys_lost", !!v)} />
                      <span className="text-sm text-foreground">All Keys Lost</span>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer p-2.5 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 transition-colors" data-testid="store-vehicle-running">
                      <Checkbox checked={form.vehicle_running} onCheckedChange={(v) => update("vehicle_running", !!v)} />
                      <span className="text-sm text-foreground">Vehicle Running</span>
                    </label>
                    <label className="flex items-center gap-2.5 cursor-pointer p-2.5 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 transition-colors" data-testid="store-has-key">
                      <Checkbox checked={form.has_key} onCheckedChange={(v) => update("has_key", !!v)} />
                      <span className="text-sm text-foreground">Has a Key</span>
                    </label>
                  </div>
                </div>
              </>
            )}

            <Separator className="bg-border" />

            {/* ── Section 5: Technician & Schedule ── */}
            <div className="space-y-3" data-testid="store-schedule-section">
              <div className="flex items-center gap-2 text-blue-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-semibold">Assignment & Schedule</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Assign Technician</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => handleTechSelect(v === "__none" ? "" : v)}>
                    <SelectTrigger className="bg-muted/50 border-border" data-testid="store-technician-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Unassigned</SelectItem>
                      {teamMembers
                        .filter(t => {
                          const tid = t.member_id || t.technician_id;
                          if (tid === form.assigned_to) return true;
                          if ((t.status || "active") === "disabled") return false;
                          return !form.store_location_id
                            || !t.location_ids || t.location_ids.length === 0
                            || t.location_ids.includes(form.store_location_id);
                        })
                        .map(t => (
                        <SelectItem key={t.member_id || t.technician_id} value={t.member_id || t.technician_id}>
                          {t.name}{(t.status || "active") === "disabled" ? " (Disabled)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Send-SMS toggle — only meaningful when a technician is assigned */}
                  <div className={`flex items-center gap-2 mt-2 px-2 py-1.5 rounded-md border border-dashed ${form.assigned_to ? "border-blue-500/30 bg-blue-500/5" : "border-border bg-muted/20 opacity-60"}`}>
                    <Switch
                      checked={!!form.send_assignment_sms}
                      onCheckedChange={(v) => update("send_assignment_sms", v)}
                      disabled={!form.assigned_to}
                      data-testid="store-send-sms-toggle"
                    />
                    <Label className="text-xs text-foreground cursor-pointer flex-1">
                      Notify technician by SMS
                    </Label>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs">Date</Label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start bg-muted/50 border-border text-left font-normal" data-testid="store-date-picker">
                        <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                        {form.scheduled_date ? format(form.scheduled_date, "MMM d, yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover border-border" align="start">
                      <Calendar
                        mode="single"
                        selected={form.scheduled_date}
                        onSelect={(d) => { update("scheduled_date", d); setDatePickerOpen(false); }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground text-xs">Time</Label>
                    <div className="flex bg-muted/50 rounded-md p-0.5 border border-border">
                      <button
                        type="button"
                        onClick={() => update("schedule_type", "exact")}
                        className={`px-2 py-0.5 text-[10px] rounded transition-colors ${(form.schedule_type || "exact") === "exact" ? "bg-[#FFCC00] text-black font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="store-schedule-type-exact"
                      >
                        Exact
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({
                          ...prev,
                          schedule_type: "between",
                          scheduled_time_end: addHoursToTime(prev.scheduled_time, prev.duration_hours || 1) || prev.scheduled_time_end,
                        }))}
                        className={`px-2 py-0.5 text-[10px] rounded transition-colors ${form.schedule_type === "between" ? "bg-[#FFCC00] text-black font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid="store-schedule-type-between"
                      >
                        Between
                      </button>
                    </div>
                  </div>
                  {form.schedule_type === "between" ? (
                    <div className="flex items-center gap-1.5">
                      <Input value={form.scheduled_time}
                        onChange={(e) => setForm(prev => ({ ...prev, scheduled_time: e.target.value, scheduled_time_end: addHoursToTime(e.target.value, prev.duration_hours || 1) || prev.scheduled_time_end }))}
                        className="bg-muted/50 border-border flex-1 min-w-0" placeholder="2:00 PM" data-testid="store-time-input" />
                      <span className="text-[10px] text-muted-foreground shrink-0">to</span>
                      <Input value={form.scheduled_time_end || ""} onChange={(e) => update("scheduled_time_end", e.target.value)}
                        className="bg-muted/50 border-border flex-1 min-w-0" placeholder="4:00 PM" data-testid="store-time-end-input" />
                    </div>
                  ) : (
                    <Input value={form.scheduled_time} onChange={(e) => update("scheduled_time", e.target.value)}
                      className="bg-muted/50 border-border" placeholder="2:00 PM" data-testid="store-time-input" />
                  )}
                </div>
              </div>

              {/* Additional Technicians (dropdown multi-select, equal commission split) */}
              {form.assigned_to && (
                <div className="space-y-1.5" data-testid="store-additional-techs-section">
                  <Label className="text-muted-foreground text-xs">Additional Technicians</Label>
                  <MultiTechSelect
                    options={teamMembers
                      .filter(t => {
                        const tid = t.member_id || t.technician_id;
                        if (tid === form.assigned_to) return false;
                        if ((t.status || "active") === "disabled") return false;
                        return !form.store_location_id
                          || !t.location_ids || t.location_ids.length === 0
                          || t.location_ids.includes(form.store_location_id)
                          || (form.additional_technician_ids || []).includes(tid);
                      })
                      .map(t => ({ id: t.member_id || t.technician_id, name: t.name }))}
                    selected={form.additional_technician_ids || []}
                    onChange={(ids) => setForm(prev => ({ ...prev, additional_technician_ids: ids }))}
                    placeholder="None"
                    triggerClassName="bg-muted/50 border-border"
                    testIdPrefix="store-additional-tech"
                  />
                  {(form.additional_technician_ids || []).length > 0 && (
                    <>
                      <TechSplitEditor
                        techs={[
                          {
                            id: form.assigned_to,
                            name: teamMembers.find(t => (t.member_id || t.technician_id) === form.assigned_to)?.name || "Primary Tech",
                            primary: true,
                          },
                          ...(form.additional_technician_ids || []).map(id => ({
                            id, name: teamMembers.find(t => (t.member_id || t.technician_id) === id)?.name || "Technician",
                          })),
                        ]}
                        mode={form.tech_commission_split_mode || "equal"}
                        unit={form.tech_commission_split_unit || "percent"}
                        percents={form.tech_commission_splits_map || {}}
                        amounts={form.tech_commission_splits_amount_map || {}}
                        onChange={({ mode, unit, percents, amounts }) => setForm(prev => ({
                          ...prev, tech_commission_split_mode: mode, tech_commission_split_unit: unit,
                          tech_commission_splits_map: percents, tech_commission_splits_amount_map: amounts,
                        }))}
                        testIdPrefix="store-tech-split"
                      />
                      <p className="text-[11px] text-blue-500" data-testid="store-multi-tech-sms-hint">
                        Assignment SMS goes to every assigned technician.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Dates (collapsible — directly below Schedule) */}
              <JobDatesSection
                createdAt={form.created_at}
                completedAt={form.completed_at}
                scheduledDate={form.scheduled_date ? format(form.scheduled_date, "MMM d, yyyy") : ""}
                isCompleted={["completed", "done"].includes((form.status || "").toLowerCase())}
                onChange={({ created_at, completed_at }) => {
                  setForm((f) => ({
                    ...f,
                    ...(created_at !== undefined ? { created_at } : {}),
                    ...(completed_at !== undefined ? { completed_at } : {}),
                  }));
                }}
              />
            </div>

            <Separator className="bg-border" />

            {/* ── Section: Job Completion (only when status is completed*) ── */}
            {(form.status === "completed" || form.status === "completed_pending_payment") && (
              <div className="rounded-xl bg-card/50 border border-border" data-testid="store-completion-section">
                <CompletionSection
                  formData={form}
                  setFormData={setForm}
                  parts={parts}
                  setParts={setParts}
                  payments={payments}
                  setPayments={setPayments}
                  teamMembers={teamMembers}
                  paymentTypes={paymentTypes}
                  editingJob={editingJob ? { ...editingJob, ...(taxSnapshot || {}) } : editingJob}
                  liveTax={taxPreview}
                  linkedInvoiceFee={linkedInvFee}
                />
              </div>
            )}

            {/* ── Recurring job (renders only when enabled in Business Settings) ── */}
            <RecurringJobPanel
              value={form}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
            />

            {/* ── Automatic tax (renders only when the business enabled it) ── */}
            <JobTaxPanel
              jobId={editingJob?.job_id}
              address={form.customer_address}
              city={form.address_city}
              state={form.address_state}
              zip={form.zip_code}
              amount={form.total_charged}
              customerId={form.customer_id}
              locationId={form.store_location_id}
              savedJob={editingJob ? { ...editingJob, ...(taxSnapshot || {}) } : editingJob}
              onTaxChange={setTaxSnapshot}
              onPreviewChange={setTaxPreview}
              taxDisabled={!!form.tax_disabled}
              onTaxDisabledChange={(v) => setForm(prev => ({ ...prev, tax_disabled: v }))}
              onOverrideDraftChange={setTaxOverrideDraft}
            />

            {/* ── Attachments ── */}
            {editingJob?.job_id ? (
              <JobAttachments jobId={editingJob.job_id} />
            ) : (
              <PendingAttachmentsPicker files={pendingFiles} onChange={setPendingFiles} />
            )}

            <Separator className="bg-border" />

            {/* ── Section 6: Notes ── */}
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs">Job Title (auto-generated if empty)</Label>
              <Input value={form.title} onChange={(e) => update("title", e.target.value)}
                className="bg-muted/50 border-border" placeholder={buildTitle()} data-testid="store-job-title" />
              <Label className="text-muted-foreground text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)}
                className="bg-muted/50 border-border min-h-[140px] resize-y" placeholder="Additional notes..." data-testid="store-job-notes" />
            </div>
    </div>
  );

  const footerBar = (
    <div className={isPage
      ? "sticky bottom-0 z-10 mt-5 py-4 border-t border-border bg-background flex items-center justify-between"
      : "px-5 py-3 border-t border-border flex items-center justify-between shrink-0"}>
      <div className="text-xs text-muted-foreground">
        {ticketPreview && <span className="font-mono bg-muted/50 px-2 py-0.5 rounded">{ticketPreview}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="store-job-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {submitting ? "Saving..." : editingJob ? "Update Job" : "Create Job"}
        </Button>
      </div>
    </div>
  );

  const addressPicker = (
    <CustomerAddressPicker
      customer={pickerCustomer}
      open={showAddressPicker}
      onClose={closeAddressPicker}
      onPick={handlePickedAddress}
    />
  );

  const linkedCustomerModal = (
    <CustomerModal
      open={!!customerModal}
      onOpenChange={(o) => { if (!o) setCustomerModal(null); }}
      customer={customerModal}
      onSaved={onLinkedCustomerSaved}
      onOpenJob={openCustomerJob}
      elevated
    />
  );

  // ── Full-page variant — no Dialog wrapper, so nested dropdowns can't
  //    fight the modal focus trap. Used by /dashboard/jobs/new + /edit. ──
  if (isPage) {
    return (
      <div data-testid="store-job-form-page-body">
        {formBody}
        {footerBar}
        {addressPicker}
        {linkedCustomerModal}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-background border-border p-0 gap-0 overflow-hidden flex flex-col !z-[60] !inset-0 !translate-x-0 !translate-y-0 !max-w-none !max-h-none !rounded-none !border-0 sm:!inset-auto sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!w-[95vw] sm:!h-auto sm:!max-w-2xl sm:!max-h-[85vh] sm:!rounded-lg sm:!border"
        data-testid="store-job-form-dialog"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0 border-b border-border">
          <DialogTitle className="text-foreground text-lg font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-500" />
            {editingJob ? "Edit Store Job" : "New Store Job"}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          {formBody}
        </ScrollArea>

        {footerBar}
      </DialogContent>
      {addressPicker}
      {linkedCustomerModal}
    </Dialog>
  );
};

export default StoreJobFormDialog;
